import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { NgForm } from "@angular/forms";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Subscription } from "rxjs";
import { Bills } from "src/model/class/general classes/bill";

import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { CriterionExpensesNoteType } from "src/model/enum/enumExpensesNote";
import { AlertService } from "src/service/alert.service";
import { DpBillsService } from "src/service/bills.service";
import { SelectFieldOption } from "src/components/general components/select-field/select-field.component";
import { EventService } from "src/service/event.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Component({
    selector: "app-popup-expenses-note",
    templateUrl: "./popup-expenses-note.component.html",
    styleUrls: ["./popup-expenses-note.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupExpensesNoteComponent implements OnInit, OnDestroy, iEvent {
  public _eventSubscriptions: Array<Subscription> = null;
  public listBills: Array<Bills> = [];
  public isBillsLoaded: boolean = false;
  public isLoading: boolean = true;
  public showErrorContent: boolean = false;
  public billDateOutputFormat =
    MomentFormats.MOMENT_DATETIME_FORMAT_DATE_YEAR;
  @Input() modalRef: ModalController;
  public criterionExpensesNoteType = Object.keys(CriterionExpensesNoteType)
    .filter((key) => !isNaN(Number(CriterionExpensesNoteType[key])))
    .map((key) => ({
      value: CriterionExpensesNoteType[key],
      label: this.getLabelCriterio(CriterionExpensesNoteType[key]),
    }));
  public criterionInvoluntaryExpensesNoteType =
    this.criterionExpensesNoteType.filter(
      (key) => key.value !== CriterionExpensesNoteType.CONSUMPTION
    );
  /** Criteria for the select fields (labels are translation keys) */
  public criterionOptions: Array<SelectFieldOption> = this.criterionExpensesNoteType.map(
    (criterion) => ({ value: criterion.value, label: "dictionary." + criterion.label })
  );
  public criterionInvoluntaryOptions: Array<SelectFieldOption> =
    this.criterionInvoluntaryExpensesNoteType.map((criterion) => ({
      value: criterion.value,
      label: "dictionary." + criterion.label,
    }));
  public selectedBills: number[] = [];
  public showButtonContinue: boolean = false;
  public selectBills: boolean = true;
  public insertData: boolean = false;
  public dateTimeOutputFormat = MomentFormats.MOMENT_DATETIME_DATE_EXTENDED;

  private _translateSubscription: Subscription = null;
  private _lblWarning: string = "";
  private _lblfillAllFieldsToContinue: string = "";
  private _lblConfirmAccounting: string = "";
  private _formData: any;
  private _lblPercVoluntaryBetween: string = "";

  constructor (
    private _modalController: ModalController,
    private _eventService: EventService,
    private _dpBillsService: DpBillsService,
    private _translateService: TranslateService,
    private _alertService: AlertService
  ) { }

  async ngOnInit() {
    this.subscribeEvents();
    await this.getBills();
    this.loadTranslation();
  }
  async ngOnChange() { }

  /**
   * Load trnaslation from translation service
   */
  public loadTranslation(): void {
    this._translateSubscription = this._translateService
      .get([
        "dictionary.attention",
        "dictionary.fillAllFieldsToContinue",
        "dictionary.confirmAccounting",
        "dictionary.percVoluntaryBetween",
      ])
      .subscribe((translation) => {
        this._lblWarning = translation["dictionary.attention"];
        this._lblfillAllFieldsToContinue =
          translation["dictionary.fillAllFieldsToContinue"];
        this._lblConfirmAccounting =
          translation["dictionary.confirmAccounting"];
        this._lblPercVoluntaryBetween =
          translation["dictionary.percVoluntaryBetween"];
      });
  }

  /**
   * Update the list of selected bills
   * @param event
   */
  public updateSelectedBills(event: any): void {
    const checkboxValue: string = event.detail.value;
    if (event.detail.checked) {
      this.selectedBills.push(Number(checkboxValue));
    } else {
      this.selectedBills = this.selectedBills.filter(
        (id) => id !== Number(checkboxValue)
      );
    }
    if (this.selectedBills.length > 0) {
      this.showButtonContinue = true;
    } else {
      this.showButtonContinue = false;
    }
  }

  /**
   * Select / deselect a bill by tapping its row
   * @param idBill
   */
  public toggleBillSelection(idBill: number): void {
    this.selectedBills = this.isBillSelected(idBill)
      ? this.selectedBills.filter((id) => id !== idBill)
      : [...this.selectedBills, idBill];
    this.showButtonContinue = this.selectedBills.length > 0;
  }

  /**
   *  Check if the bill is selected
   * @param billId
   * @returns
   */
  public isBillSelected(idBill: number): boolean {
    return this.selectedBills.includes(idBill);
  }

  /**
   * Create accounting
   */
  public async onClickCreateExpensesNote(expenseForm: NgForm): Promise<void> {
    if (expenseForm.valid) {
      this._formData = expenseForm.value;
      if (
        this._formData.voluntaryExpenses > 100 ||
        this._formData.voluntaryExpenses < 0
      ) {
        this._alertService.presentAlertOk(
          this._lblWarning,
          "",
          this._lblPercVoluntaryBetween
        );
        return;
      }

      if (
        await this._alertService.presentAlertConfirmOrDismiss(
          "",
          this._lblConfirmAccounting,
          ""
        )
      ) {
        this.modalRef.dismiss({
          selectedBills: [this._formData, this.selectedBills],
        });
      }
    } else {
      this._alertService.presentAlertOk(
        this._lblWarning,
        "",
        this._lblfillAllFieldsToContinue
      );
    }
  }

  /**
   * Get the consumption kind label
   * @param consumptionKind
   * @returns
   */
  public getConsumption(consumptionKind) {
    return this._translateService.instant(
      "dictionary." +
      StaticClass.getConsumptionKindLabel(consumptionKind)?.toLowerCase()
    );
  }
  /**
   * Show next section and hide list of bills
   */
  public onClickNextPage(nextPage: boolean) {
    this.selectBills = !nextPage;
    this.insertData = nextPage;
  }

  /**
   * Get the bills from the server
   */
  private async getBills(): Promise<void> {
    try {
      this.listBills = [];
      const responseData: Array<Bills> | number =
        await this._dpBillsService.getAllBills(true);
      if (typeof responseData !== "number") {
        this.listBills = this.sortByDate(responseData);
        this.isBillsLoaded = true;
      } else {
        this.isBillsLoaded = true;
        this.isLoading = false;
      }
    } catch (error) {
      this.showErrorContent = true;
      this.isLoading = false;
      this.isBillsLoaded = true;
    }
  }

  /**
   * Get the label of the criterion
   * @param criterion
   * @returns
   */
  private getLabelCriterio(criterion: CriterionExpensesNoteType): string {
    if (criterion == CriterionExpensesNoteType.CONSUMPTION) {
      return "consumption";
    }
    if (criterion == CriterionExpensesNoteType.THOUSANDTHS) {
      return "thousandthsUNI";
    }
    if (criterion == CriterionExpensesNoteType.THOUSANDTHS_HEATING) {
      return "thousandthsHeating";
    }
  }

  /**
   * Sort the bills by date in descending order, so the latest first
   * @param bills  array of bill
   * @returns array of sorted bills
   */
  private sortByDate(bills: Array<Bills>): Array<Bills> {
    return bills.sort((billA: Bills, billB: Bills) => {
      const dateA = moment(billA.date);
      const dateB = moment(billB.date);
      return dateB.diff(dateA);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeEvents();
  }

  /**
   * Close modal and return index selected
   * @param valSelected
   */
  public closeModal() {
    this.modalRef.dismiss();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalController.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
    this._translateSubscription?.unsubscribe();
  }
  //#endregion
}
